import {
	FormControl,
	FormHelperText,
	InputLabel,
	TextField,
	TextFieldProps,
	Typography,
} from '@mui/material';
import { FormikContextType } from 'formik';
import cn from 'classnames';
import { ReactNode, useId } from 'react';
import { FieldAdditionalProps } from 'shared/_ui/form/types';
import { getFieldGridSx } from 'shared/_ui/form/utils';
import { mergeSx } from 'shared/_ui/utils';

function TextFieldStyled(props: TextFieldProps & FieldAdditionalProps) {
	const {
		className,
		labelProps,
		readOnly = false,
		onReadOnlyClick,
		onClick,
		...fieldProps
	} = props;
	const { label, helperText } = fieldProps;

	const { InputProps, name, sx, fullWidth } = fieldProps;

	// Use grid to move label to the correct position under the value
	const sxProps = mergeSx(sx, {
		'&.field': {
			...(fullWidth ? { width: '100%' } : {}),
			...getFieldGridSx({ label, labelProps, helperText }),
		},
		'.MuiFormLabel-root': {
			gridArea: 'label',
			...(labelProps?.width ? { width: labelProps.width } : {}),
			maxWidth: '100%',
		},
		'.MuiInputBase-input': {
			gridArea: 'value',
		},
		'.MuiFormHelperText-root': {
			gridArea: 'help',
		},
	});

	const id = useId();

	return (
		<TextField
			id={name ?? id}
			{...fieldProps}
			onClick={readOnly ? onReadOnlyClick ?? onClick : onClick}
			className={cn('field', 'editable', className)}
			InputProps={{ readOnly, ...InputProps }}
			sx={sxProps}
		/>
	);
}

type CommonTextFieldProps = Omit<TextFieldProps, 'value' | 'onChange' | 'onBlur'> &
	FieldAdditionalProps;

type FormTextFieldProps = CommonTextFieldProps & {
	formik: FormikContextType<any>;
	readOnly?: boolean;
	onReadOnlyClick?: () => void;
	name: string;
};

type FieldTextFieldProps = Pick<
	TextFieldProps,
	'className' | 'name' | 'label' | 'sx' | 'fullWidth' | 'error' | 'helperText'
> &
	FieldAdditionalProps & {
		value: ReactNode;
	};

export function FieldTextField(props: FieldTextFieldProps) {
	const { className, name, sx, label, labelProps, fullWidth, value, helperText, ...fieldProps } =
		props;
	const { error } = fieldProps;

	const sxLabel = mergeSx(
		sx?.['.MuiFormLabel-root' as keyof typeof sx],
		labelProps?.width ? { width: labelProps.width } : {}
	);

	// Use grid to move label to the correct position under the value
	const sxProps = mergeSx(sx, {
		width: fullWidth ? '100%' : 'fit-content',
		...getFieldGridSx({ label, labelProps, helperText }),
		'.MuiFormLabel-root': {
			gridArea: 'label',
			maxWidth: '100%',
		},
		'.MuiTypography-root': {
			gridArea: 'value',
		},
		'.MuiFormHelperText-root': {
			gridArea: 'help',
		},
	});

	return (
		<FormControl
			className={cn('field', className)}
			sx={sxProps}
			fullWidth={fullWidth}
			{...fieldProps}
		>
			{!!label && (
				<InputLabel htmlFor={name} sx={sxLabel} id={`${name}-label`}>
					{label}
				</InputLabel>
			)}
			<Typography
				id={name}
				display="inline-block"
				width={fullWidth ? '100%' : undefined}
				flex={fullWidth ? 1 : undefined}
			>
				{value}
			</Typography>
			{helperText && (
				<FormHelperText id={`${name}-helper-text`} error={error} sx={{ flexBasis: '100%' }}>
					{helperText}
				</FormHelperText>
			)}
		</FormControl>
	);
}

export function FormTextField({ formik, ...props }: FormTextFieldProps) {
	const { name } = props;

	const helperText = formik.errors[name] as TextFieldProps['helperText']; // formik.touched[name] &&
	const error = Boolean(formik.errors[name]); // formik.touched[name] &&

	return (
		<TextFieldStyled
			id={name}
			{...props}
			value={formik.values[name]}
			onChange={formik.handleChange}
			onBlur={formik.handleBlur}
			error={error}
			helperText={helperText}
		/>
	);
}
